// ─────────────────────────────────────────────
//  AssignmentGrid — the day's assignment table (Phase 4 D-01, D-01b, D-09)
//  Data-driven: sections come from the facility's own Locations + Rooms
//  (Facility Settings → Room Management), grouped exactly like the OR Board,
//  with a trailing OTHER section for any assignment whose orRoom matches no
//  current room. One row per room.
//  Every room row is permanently editable (dropdowns + per-row Save) so the
//  whole day can be filled in and saved room by room — no Edit step.
//  "Add Row" appends free-orRoom pending rows that land in OTHER once saved.
//  Any doctor may edit; every save notifies the AIC (Phase-3 rule).
// ─────────────────────────────────────────────
import React, { useEffect, useRef, useState } from "react";
import tokens from "../theme/tokens";
import Button from "./Button";
import Modal from "./Modal";
import AssignmentRow from "./AssignmentRow";
import CrossFacilityPicker from "./CrossFacilityPicker";
import { useIsMobile } from "../hooks/useIsMobile";
import {
  GRID_COLUMNS,
  buildAssignmentSections,
  roomForOrRoom,
} from "../domain/gridTemplate";
import { crnaRoomConflicts } from "../domain/crnaRoomGuard";
import {
  createAssignment,
  updateAssignmentRow,
  deleteAssignmentRow,
  setRoomAssignment,
  clearRoom,
  listProcedureRefs,
  notifyAIC,
} from "../firebase/firestore";

const { color, font, space } = tokens;

// ── AIC bar ──────────────────────────────────
function AicBar({ aicId, doctors }) {
  const aic = doctors.find((d) => d.id === aicId);
  return (
    <div style={st.aicBar} aria-label="AIC">
      <span style={st.aicLabel}>AIC:</span>{" "}
      {aic ? (
        <span style={st.aicName}>{aic.name}</span>
      ) : (
        <span style={st.aicNotSet}>— not set</span>
      )}
    </div>
  );
}

// ── Section header row ────────────────────────
function SectionHeaderRow({ label, colSpan }) {
  return (
    <tr>
      <td colSpan={colSpan} style={st.sectionHeader}>
        {label}
      </td>
    </tr>
  );
}

export default function AssignmentGrid({
  facilityId,
  date,
  assignments = [],
  rooms = [],       // facility rooms (from Room Management); section source
  locations = [],   // facility locations (from Facility Settings); section order
  doctors = [],
  crnas = [],
  aicId = null,
  actor,
  facilityOptions = [],
  roster = { supervisors: [], crnas: [] },
  onMemberPatch,
  hiddenRooms = [], // Gap 5b: rooms hidden via Remove on doc-less rows
  onHideRoom,      // (room) => void — persists hide to daily_boards
  onUnhideRoom,    // (room) => void — persists unhide to daily_boards
  showCrossFacilityButton = true,
}) {
  const isMobile = useIsMobile();
  const [refs, setRefs] = useState({ surgeons: [], procedures: [] });
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickedRoom, setPickedRoom] = useState("");
  const [removeError, setRemoveError] = useState("");
  // Pending destructive-remove confirmation for a SAVED row (carries data).
  // Empty template rows skip this and hide instantly (see requestRemove).
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [removing, setRemoving] = useState(false);
  // Pending free rows added via "Add Row" — discarded on Remove, dropped
  // automatically once their save lands (the snapshot brings the real doc).
  const [pendingRows, setPendingRows] = useState([]);
  const nextKeyRef = useRef(0);

  // Load surgeon/procedure autocomplete bag once per facility (D-01b).
  useEffect(() => {
    if (!facilityId) return;
    let cancelled = false;
    listProcedureRefs(facilityId)
      .then((r) => { if (!cancelled) setRefs(r); })
      .catch((err) => console.error("Load procedure refs failed:", err));
    return () => { cancelled = true; };
  }, [facilityId]);

  // selectedOr pre-fills orRoom; un-hides the room if it was previously hidden.
  const startAdd = (selectedOr = "") => {
    if (selectedOr && hiddenRooms.includes(selectedOr)) onUnhideRoom && onUnhideRoom(selectedOr);
    setPendingRows((rows) => [
      ...rows,
      { key: `pending-${nextKeyRef.current++}`, orRoom: selectedOr },
    ]);
  };

  const removePending = (key) =>
    setPendingRows((rows) => rows.filter((r) => r.key !== key));

  // True when an assignment doc carries real, losable data (not just an empty
  // materialized shell). Mirrors saveRow's data fields.
  const rowHasData = (a) =>
    !!(a &&
      (a.surgeon ||
        a.procedure ||
        a.startTime ||
        a.endTime ||
        a.attendingId ||
        a.ahpId ||
        a.crnaReliefId ||
        a.mdReliefId));

  // Remove is available on every row. A row carrying data routes through a
  // confirm modal first — deleting it frees its CRNA/MD and is irreversible.
  // An empty materialized doc deletes directly; a doc-less template row hides
  // instantly — both carry nothing, so they keep the quick add/hide flow.
  const requestRemove = (a, orRoom) => {
    if (a?.id && rowHasData(a)) {
      setConfirmTarget({
        id: a.id,
        orRoom,
        surgeon: a.surgeon || "",
        attendingName: a.attendingName || "",
        ahpName: a.ahpName || "",
      });
    } else {
      handleRemove(a?.id || null, orRoom);
    }
  };

  // Confirm and execute a pending destructive remove (saved row only).
  const confirmRemove = async () => {
    if (!confirmTarget) return;
    setRemoving(true);
    try {
      await handleRemove(confirmTarget.id, confirmTarget.orRoom);
    } finally {
      setRemoving(false);
      setConfirmTarget(null);
    }
  };

  // Doc id present → delete the doc, which automatically frees the assigned
  // CRNA and MD/DO (every list derives from assignment docs); no id → hide the
  // template room. Reached directly for empty rows; via confirmRemove for saved.
  const handleRemove = async (assignmentId, orRoom) => {
    setRemoveError("");
    try {
      if (assignmentId) {
        await deleteAssignmentRow(assignmentId);
      } else if (orRoom && onHideRoom) {
        await onHideRoom(orRoom);
      }
      // Reset the live room doc so the OR Board card clears too — parity with
      // the board's ✕ (clearRoom). Without this, removing a grid row leaves the
      // mirrored staff on the board. Only acts on rows mapping to a real room.
      const room = roomForOrRoom(orRoom, rooms);
      if (room) await clearRoom(room.id);
      // Best-effort AIC notification — never blocks the remove path.
      try {
        await notifyAIC({
          facilityId,
          actor: { uid: actor?.uid, name: actor?.name },
          summary: `removed the OR ${orRoom || "?"} row${assignmentId ? " and freed its staff" : ""}`,
        });
      } catch (notifyErr) {
        console.error("Notify AIC (remove) failed:", notifyErr);
      }
    } catch (err) {
      console.error("Remove assignment failed:", err);
      setRemoveError("Couldn't remove. Check your connection and try again.");
    }
  };

  // Sections come from the facility's own Locations + Rooms (Facility Settings),
  // grouped exactly like the OR Board. Hidden rooms (Gap 5b) drop out of their
  // section but stay re-addable below; OTHER is never hidden.
  const allSections = buildAssignmentSections(rooms, locations, assignments);
  const sections = allSections.map((s) =>
    s.key === "other"
      ? s
      : { ...s, rows: s.rows.filter(({ or }) => !hiddenRooms.includes(or)) }
  );

  // Add-row picker offers facility rooms with no assignment yet. Matching is by
  // name/alias via the section build, so legacy orRoom values still count.
  const assignedRoomNames = new Set(
    allSections
      .filter((s) => s.key !== "other")
      .flatMap((s) => s.rows)
      .filter((r) => r.assignment)
      .map((r) => r.or)
  );
  const availableRooms = rooms
    .map((r) => r.name)
    .filter((name) => !assignedRoomNames.has(name));

  // Persist one row (create or update). Thrown errors surface in the row.
  // Saved display names fall back to the previously stored value so a staff
  // member who left the roster isn't silently wiped from an untouched field.
  const saveRow = async (row, existingId) => {
    const attending = doctors.find((d) => d.id === row.attendingId);
    const ahp = crnas.find((c) => c.id === row.ahpId);
    const crnaRelief = crnas.find((c) => c.id === row.crnaReliefId);
    const mdRelief = doctors.find((d) => d.id === row.mdReliefId);
    const data = {
      facilityId,
      date,
      orRoom: row.orRoom || "",
      surgeon: (row.surgeon || "").trim(),
      procedure: (row.procedure || "").trim(),
      startTime: row.startTime || "",
      endTime: row.endTime || "",
      attendingId: row.attendingId || null,
      attendingName: attending?.name || row.attendingName || "",
      ahpId: row.ahpId || null,
      ahpName: ahp?.name || row.ahpName || "",
      ahpShiftCode: ahp?.shiftCode ?? row.ahpShiftCode ?? null,
      ahpDepartTime: row.ahpDepartTime || "",
      crnaReliefId: row.crnaReliefId || null,
      crnaReliefName: crnaRelief?.name || row.crnaReliefName || "",
      mdReliefId: row.mdReliefId || null,
      mdReliefName: mdRelief?.name || row.mdReliefName || "",
      source: "manual",
    };
    if (existingId) {
      await updateAssignmentRow(existingId, data);
    } else {
      await createAssignment(data);
    }

    // Mirror the saved staff onto the live ROOM doc so the OR Board reflects
    // Daily-grid assignments — parity with AssignmentModal, which already does
    // this. Without it the assignments collection and the rooms collection
    // drift: a grid-saved room shows "Empty" on the board (the P0 sync bug).
    // Only rows that map to a real room mirror; free OTHER rows have none.
    // Skip rows with no staff so we never flip an empty room to "ready" on a
    // blank save, and never downgrade a room a provider has already signed
    // into (status "active") — setRoomAssignment otherwise forces "ready".
    const room = roomForOrRoom(data.orRoom, rooms);
    if (room && (data.ahpId || data.attendingId)) {
      await setRoomAssignment(room.id, {
        providerId: data.ahpId || null,
        providerName: data.ahpName || "",
        providerRole: ahp?.subRole ? ahp.subRole.toUpperCase() : ahp?.role || "",
        supervisingDoctorId: data.attendingId || null,
        supervisorName: data.attendingName || "",
        scheduledStartTime: data.startTime || "",
        ...(room.status === "active" ? { status: "active" } : {}),
      });
    }

    // Any doctor may edit; the AIC is notified of the change (D-01b).
    // Best-effort — a notify failure never rolls back the save.
    try {
      await notifyAIC({
        facilityId,
        actor: { uid: actor?.uid, name: actor?.name },
        summary: `${existingId ? "updated" : "added"} the OR ${data.orRoom || "?"} assignment row`,
      });
    } catch (notifyErr) {
      console.error("Notify AIC failed:", notifyErr);
    }
  };

  // Cross-facility section JSX — same for mobile/desktop, only button size differs.
  const crossFacilityBlock = showCrossFacilityButton ? (
    <>
      <div style={{ marginTop: space.md }}>
        <Button variant="secondary" size={isMobile ? "full" : "md"} onClick={() => setPickerOpen(true)}>
          Add Staff from Another Facility
        </Button>
      </div>
      <CrossFacilityPicker
        isOpen={pickerOpen}
        onClose={() => setPickerOpen(false)}
        facilityOptions={facilityOptions}
        currentFacilityId={facilityId}
        date={date}
        actor={actor}
        roster={roster}
      />
    </>
  ) : null;

  // Staff already booked as a primary on a SAVED row — excluded from every
  // OTHER row's primary CRNA / doctor dropdown so nobody is double-booked. A
  // row still shows its own current pick (the cell builder keeps `own` ids).
  const assignedAhpIds = new Set(assignments.map((a) => a.ahpId).filter(Boolean));
  const assignedAttendingIds = new Set(assignments.map((a) => a.attendingId).filter(Boolean));

  const rowProps = (a, templateOr, isTemplateLocked) => ({
    assignment: a,
    doctors,
    crnas,
    surgeons: refs.surgeons,
    assignedAhpIds,
    assignedAttendingIds,
    onSaveRow: saveRow,
    onRemove: () => requestRemove(a, templateOr),
    templateOr: templateOr || null,
    isTemplateLocked: !!isTemplateLocked,
    isMobile,
    onMemberPatch,
  });

  // Pending rows save like any row, then drop their placeholder — the
  // Firestore snapshot re-delivers the saved doc into its section.
  const pendingRowProps = (p) => ({
    assignment: null,
    doctors,
    crnas,
    surgeons: refs.surgeons,
    assignedAhpIds,
    assignedAttendingIds,
    onSaveRow: (row, id) => saveRow(row, id).then(() => removePending(p.key)),
    onRemove: () => removePending(p.key),
    templateOr: p.orRoom,
    isTemplateLocked: false,
    isMobile,
    onMemberPatch,
  });

  const totalCols = GRID_COLUMNS.length + 1;

  const toolbar = (
    <div style={st.toolbar}>
      <select
        aria-label="Room for new row"
        value={pickedRoom}
        onChange={(e) => setPickedRoom(e.target.value)}
        style={st.roomSelect}
      >
        <option value="">— other —</option>
        {availableRooms.map((or) => (
          <option key={or} value={or}>{or}</option>
        ))}
      </select>
      <Button onClick={() => { startAdd(pickedRoom); setPickedRoom(""); }}>Add Row</Button>
    </div>
  );

  const removeErrorLine = removeError ? (
    <div style={st.removeError} role="alert">{removeError}</div>
  ) : null;

  // Double-booking warning: a non-OB CRNA is the primary in more than one room.
  // OB CRNAs may cover several rooms, so they're never flagged. This WARNS only
  // — saving is never blocked; the AIC/doctor may override intentionally.
  const roomConflicts = crnaRoomConflicts(assignments, rooms);
  const conflictBanner = roomConflicts.length ? (
    <div style={st.conflictBanner} role="status">
      <strong>⚠ Double-booked CRNA{roomConflicts.length > 1 ? "s" : ""}:</strong>{" "}
      {roomConflicts
        .map((c) => `${c.ahpName || "A CRNA"} (${c.rooms.join(" + ")})`)
        .join("; ")}
      . Only OB CRNAs cover multiple rooms — fix or override.
    </div>
  ) : null;

  // Destructive remove confirmation for a saved row (never a native dialog).
  const confirmModal = (
    <Modal
      isOpen={!!confirmTarget}
      onClose={() => !removing && setConfirmTarget(null)}
      title="Remove this assignment?"
      footer={
        <>
          <Button variant="secondary" onClick={() => setConfirmTarget(null)} disabled={removing}>
            Cancel
          </Button>
          <Button variant="danger" onClick={confirmRemove} disabled={removing}>
            {removing ? "Removing…" : "Remove assignment"}
          </Button>
        </>
      }
    >
      {confirmTarget ? (
        <>
          OR {confirmTarget.orRoom || "?"}: this deletes the saved assignment
          {confirmTarget.surgeon || confirmTarget.attendingName || confirmTarget.ahpName ? (
            <>
              {" "}({[confirmTarget.surgeon, confirmTarget.attendingName, confirmTarget.ahpName]
                .filter(Boolean)
                .join(", ")})
            </>
          ) : null}{" "}
          and frees its CRNA and doctor for other rooms. This can't be undone.
        </>
      ) : null}
    </Modal>
  );

  if (isMobile) {
    return (
      <section aria-label="Assignment grid" style={st.section}>
        {toolbar}
        <AicBar aicId={aicId} doctors={doctors} />
        {removeErrorLine}
        {conflictBanner}
        {sections.map((section) => (
          <div key={section.key}>
            <div style={st.mobileSectionHeader}>{section.label}</div>
            {section.rows.map(({ or, assignment }) => {
              const isTemplate = section.key !== "other";
              return (
                <AssignmentRow
                  key={assignment?.id || `template-${or}`}
                  {...rowProps(assignment, or, isTemplate)}
                />
              );
            })}
          </div>
        ))}
        {pendingRows.map((p) => (
          <AssignmentRow key={p.key} {...pendingRowProps(p)} />
        ))}
        {crossFacilityBlock}
        {confirmModal}
      </section>
    );
  }

  return (
    <section aria-label="Assignment grid" style={st.section}>
      {toolbar}
      <AicBar aicId={aicId} doctors={doctors} />
      {removeErrorLine}
      {conflictBanner}
      <div style={st.scrollWrap}>
        <table aria-label="Daily OR Assignments" style={st.table}>
          <thead>
            <tr style={st.headRow}>
              {GRID_COLUMNS.map((c, i) => (
                // Two RELIEF columns have the same label — use index as part of key
                <th key={`${c}-${i}`} scope="col" style={st.th}>{c}</th>
              ))}
              <th scope="col" style={st.th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {sections.map((section) => (
              <React.Fragment key={section.key}>
                <SectionHeaderRow label={section.label} colSpan={totalCols} />
                {section.rows.map(({ or, assignment }) => {
                  const isTemplate = section.key !== "other";
                  return (
                    <AssignmentRow
                      key={assignment?.id || `template-${section.key}-${or}`}
                      {...rowProps(assignment, or, isTemplate)}
                    />
                  );
                })}
              </React.Fragment>
            ))}
            {pendingRows.map((p) => (
              <AssignmentRow key={p.key} {...pendingRowProps(p)} />
            ))}
          </tbody>
        </table>
      </div>
      {crossFacilityBlock}
      {confirmModal}
    </section>
  );
}

const st = {
  section: {
    fontFamily: font.family,
  },
  toolbar: {
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    gap: space.sm,
    marginBottom: space.sm,
  },
  roomSelect: {
    fontFamily: font.family,
    fontSize: font.size.body,
    color: color.text.primary,
    background: color.surface.card,
    border: `1px solid ${color.border.default}`,
    borderRadius: 6,
    padding: `${space.xs}px ${space.sm}px`,
    minHeight: 44,
  },
  // AIC bar — card header strip above the table.
  aicBar: {
    background: color.surface.raised,
    border: `1px solid ${color.border.default}`,
    borderRadius: 8,
    padding: `${space.sm}px ${space.md}px`,
    marginBottom: space.sm,
    fontSize: font.size.body,
    display: "flex",
    alignItems: "center",
    gap: space.xs,
  },
  aicLabel: {
    fontWeight: font.weight.bold,
    color: color.text.secondary,
    fontSize: font.size.body,
  },
  aicName: {
    color: color.text.primary,
    fontWeight: font.weight.bold,
    fontSize: font.size.body,
  },
  aicNotSet: {
    color: color.text.muted,
    fontStyle: "italic",
    fontSize: font.size.body,
  },
  removeError: {
    color: color.accent.error,
    fontSize: font.size.label,
    marginBottom: space.sm,
  },
  conflictBanner: {
    color: "#7a4f00",
    background: "#fff4d6",
    border: "1px solid #e0b84c",
    borderRadius: 8,
    padding: `${space.sm}px ${space.md}px`,
    fontSize: font.size.label,
    marginBottom: space.sm,
  },
  scrollWrap: {
    overflowX: "auto",
    background: color.surface.card,
    border: `1px solid ${color.border.default}`,
    borderRadius: 12,
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
  },
  headRow: {
    background: color.surface.raised,
    borderBottom: `1px solid ${color.border.default}`,
  },
  th: {
    padding: `5px ${space.xs}px`,
    textAlign: "left",
    fontSize: font.size.label,
    fontWeight: font.weight.bold,
    color: color.text.secondary,
    textTransform: "uppercase",
    letterSpacing: "0.4px",
  },
  // Section divider row — full-width, raised surface, section label.
  sectionHeader: {
    padding: `${space.xs}px ${space.md}px`,
    background: color.surface.raised,
    borderTop: `2px solid ${color.border.default}`,
    borderBottom: `1px solid ${color.border.default}`,
    fontSize: font.size.label,
    fontWeight: font.weight.bold,
    color: color.text.secondary,
    textTransform: "uppercase",
    letterSpacing: "1px",
  },
  // Mobile section header (non-table layout).
  mobileSectionHeader: {
    padding: `${space.xs}px ${space.sm}px`,
    background: color.surface.raised,
    border: `1px solid ${color.border.default}`,
    borderRadius: 6,
    marginBottom: space.xs,
    marginTop: space.sm,
    fontSize: font.size.label,
    fontWeight: font.weight.bold,
    color: color.text.secondary,
    textTransform: "uppercase",
    letterSpacing: "1px",
  },
};
